import React, {useEffect, useRef, useState} from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

export type V2AssistantMessage = {
    id: string;
    role: "assistant" | "user";
    text: string;
};

type V2AssistantSidebarProps = {
    messages: V2AssistantMessage[];
    onSend: (message: string) => void | Promise<void>;
    isSending?: boolean;
    error?: string | null;
    onClose?: (() => void) | null;
    emptyDescription?: string;
    placeholder?: string;
};

const V2AssistantSidebar: React.FC<V2AssistantSidebarProps> = ({
    messages,
    onSend,
    isSending = false,
    error = null,
    onClose = null,
    emptyDescription = "Ask a question about Libretto and I’ll show you the steps.",
    placeholder = "Ask how to do something",
}) => {
    const [draft, setDraft] = useState("");
    const messagesEndRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({block: "end"});
    }, [messages, isSending]);

    const send = () => {
        const message = draft.trim();
        if (!message || isSending) return;
        void onSend(message);
        setDraft("");
    };

    return (
        <Box
            data-tour="assistant-panel"
            sx={{
                height: "100%",
                flex: 1,
                minHeight: 0,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                p: "3px",
            }}
        >
            <Box sx={{display: "flex", alignItems: "center", ml: "8px"}}>
                <Box sx={{
                    width: "24px",
                    height: "24px",
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "7px",
                    backgroundColor: "var(--app-bg-subtle)",
                }}>
                    <AutoAwesomeRoundedIcon sx={{fontSize: 15, color: "#F05A3C"}}/>
                </Box>
                <Typography sx={{
                    ml: 1,
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "var(--app-text-primary)",
                }}>
                    Libretto
                </Typography>
                <Box sx={{
                    ml: 0.75,
                    px: 0.625,
                    py: 0.125,
                    borderRadius: "5px",
                    backgroundColor: "var(--app-bg-subtle)",
                    color: "var(--app-text-muted)",
                    fontFamily: "Inter",
                    fontSize: "9px",
                    lineHeight: "13px",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                }}>
                    AI
                </Box>
                {onClose && (
                    <IconButton
                        aria-label="Close assistant"
                        onClick={onClose}
                        size="small"
                        sx={{ml: "auto", color: "var(--app-text-muted)"}}
                    >
                        <CloseRoundedIcon sx={{fontSize: 18}}/>
                    </IconButton>
                )}
            </Box>

            <Box sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                border: "0.5px solid var(--app-border-subtle)",
                borderRadius: "12px",
                backgroundColor: "var(--app-bg-surface)",
                overflow: "hidden",
            }}>
                <Box sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    p: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}>
                    {messages.length === 0 ? (
                        <Box sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            px: 2,
                        }}>
                            <AutoAwesomeRoundedIcon sx={{fontSize: 22, color: "#F05A3C", mb: 1}}/>
                            <Typography sx={{
                                fontFamily: "Inter",
                                fontSize: "13px",
                                fontWeight: 500,
                                color: "var(--app-text-primary)",
                            }}>
                                How can I help?
                            </Typography>
                            <Typography sx={{
                                mt: 0.5,
                                fontFamily: "Inter",
                                fontSize: "12px",
                                lineHeight: "18px",
                                color: "var(--app-text-muted)",
                            }}>
                                {emptyDescription}
                            </Typography>
                        </Box>
                    ) : messages.map((message) => (
                        <Box
                            key={message.id}
                            sx={{
                                alignSelf: message.role === "user" ? "flex-end" : "flex-start",
                                maxWidth: "88%",
                                px: 1.25,
                                py: 0.875,
                                borderRadius: "12px",
                                backgroundColor: message.role === "user"
                                    ? "var(--app-text-primary)"
                                    : "var(--app-bg-subtle)",
                                color: message.role === "user"
                                    ? "var(--app-bg-surface)"
                                    : "var(--app-text-primary)",
                            }}
                        >
                            <Typography sx={{
                                fontFamily: "Inter",
                                fontSize: "13px",
                                lineHeight: "19px",
                                whiteSpace: "pre-wrap",
                                overflowWrap: "anywhere",
                            }}>
                                {message.text}
                            </Typography>
                        </Box>
                    ))}
                    {isSending && (
                        <Box
                            aria-label="Assistant is thinking"
                            sx={{
                                alignSelf: "flex-start",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1.25,
                                py: 1,
                                borderRadius: "12px",
                                backgroundColor: "var(--app-bg-subtle)",
                            }}
                        >
                            <CircularProgress size={13} thickness={5}/>
                            <Typography sx={{
                                fontFamily: "Inter",
                                fontSize: "12px",
                                color: "var(--app-text-muted)",
                            }}>
                                Thinking…
                            </Typography>
                        </Box>
                    )}
                    <Box ref={messagesEndRef}/>
                </Box>

                <Box sx={{p: 1, borderTop: "1px solid var(--app-border-subtle)"}}>
                    {error && (
                        <Typography
                            role="alert"
                            sx={{
                                px: 0.5,
                                pb: 1,
                                fontFamily: "Inter",
                                fontSize: "12px",
                                lineHeight: "17px",
                                color: "var(--app-status-danger, #c62828)",
                            }}
                        >
                            {error}
                        </Typography>
                    )}
                    <Box
                        component="form"
                        onSubmit={(event) => {
                            event.preventDefault();
                            send();
                        }}
                        sx={{
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 0.75,
                            p: 0.75,
                            border: "1px solid var(--app-border-muted)",
                            borderRadius: "12px",
                            backgroundColor: "var(--app-bg-surface)",
                            transition: "border-color 140ms ease, box-shadow 140ms ease",
                            "&:focus-within": {
                                borderColor: "var(--app-text-primary)",
                                boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.08)",
                            },
                        }}
                    >
                        <Box sx={{
                            width: 24,
                            height: 32,
                            flex: "0 0 24px",
                            display: "grid",
                            placeItems: "center",
                            color: "var(--app-text-muted)",
                        }}>
                            <AutoAwesomeRoundedIcon sx={{fontSize: 15, color: "#F05A3C"}}/>
                        </Box>
                        <TextField
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" && !event.shiftKey) {
                                    event.preventDefault();
                                    send();
                                }
                            }}
                            placeholder={placeholder}
                            variant="standard"
                            fullWidth
                            multiline
                            minRows={1}
                            inputProps={{
                                maxLength: 1000,
                                "aria-label": "Message the assistant",
                                style: {overflowY: "hidden"},
                            }}
                            InputProps={{disableUnderline: true}}
                            sx={{
                                py: 0.5,
                                pl: 0.5,
                                "& .MuiInputBase-root": {
                                    alignItems: "flex-start",
                                    fontFamily: "Inter",
                                    fontSize: "13px",
                                    lineHeight: "19px",
                                    color: "var(--app-text-primary)",
                                },
                                "& textarea::placeholder": {
                                    color: "var(--app-text-subtle)",
                                    opacity: 1,
                                },
                            }}
                        />
                        <IconButton
                            type="submit"
                            aria-label="Send message"
                            disabled={!draft.trim() || isSending}
                            sx={{
                                width: 32,
                                height: 32,
                                flex: "0 0 32px",
                                color: "var(--app-bg-surface)",
                                backgroundColor: "var(--app-text-primary)",
                                "&:hover": {backgroundColor: "var(--app-text-primary)"},
                                "&.Mui-disabled": {
                                    color: "var(--app-text-subtle)",
                                    backgroundColor: "var(--app-bg-subtle)",
                                },
                            }}
                        >
                            <ArrowUpwardRoundedIcon sx={{fontSize: 18}}/>
                        </IconButton>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default V2AssistantSidebar;
